// Typed Governance & Compliance client (APP-004 R3).
//
// Mirrors the frozen Policy AI R3 contract (v0.3.0-governance-compliance). All
// calls go through the Studio Worker proxy
// (`/organizations/{orgId}/policy-ai/...`), which is the authorization and
// entitlement boundary; the browser never talks to the Policy AI backend
// directly and never holds a service credential.
//
// This module carries values and the backend's own explanations. It does NOT
// reimplement scoring, mapping, diffing or readiness logic — anything the
// backend computes is displayed as received.
//
// Transport lives in governanceHttp.ts and is shared with every other family
// client, so this file only declares dashboard and reporting routes.
import { GovernanceHttp, queryString } from "./governanceHttp";
import type {
  DownloadedArtifact,
  GovernanceClientOptions,
  GovernanceResponse,
} from "./governanceHttp";

export type { DownloadedArtifact, GovernanceClientOptions, GovernanceResponse };

// ----- Dashboards ----------------------------------------------------------

export interface PolicyHealthMetrics {
  total: number;
  active: number;
  draft: number;
  underReview: number;
  archived: number;
  publishedPolicies: number;
  unowned: number;
  reviewOverdue: number;
}

export interface FrameworkCoverageMetric {
  frameworkVersionId: string;
  frameworkKey: string;
  versionLabel: string;
  requirementCount: number;
  mappedCount: number;
  verifiedCount: number;
  evidencedCount: number;
  coverageRatio: number;
}

export interface EvidenceMetrics {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  expired: number;
  freshApproved: number;
  reviewOverdue: number;
  expiringWithin30Days: number;
  freshnessRatio: number;
  agingBuckets: {
    current: number;
    days31To90: number;
    days91To180: number;
    over180Days: number;
  };
}

export interface ExceptionMetrics {
  active: number;
  requested: number;
  expired: number;
  revoked: number;
  highOrCritical: number;
  expiringWithin30Days: number;
  reviewOverdue: number;
  openedLast90Days: number;
  closedLast90Days: number;
}

export interface ReadinessMetric {
  auditProjectId: string;
  name: string;
  status: string;
  latestScore: number | null;
  calculatorVersion: string | null;
  snapshotAt: string | null;
}

export interface FindingMetrics {
  total: number;
  open: number;
  inRemediation: number;
  disputed: number;
  resolved: number;
  accepted: number;
  overdue: number;
  critical: number;
  high: number;
  raisedLast90Days: number;
  closedLast90Days: number;
}

export interface RegulatoryMetrics {
  regulations: number;
  activeVersions: number;
  assessments: number;
  assessmentsInReview: number;
  impactItems: number;
  unverifiedItems: number;
  updateRequiredItems: number;
}

export interface RemediationMetrics {
  open: number;
  inProgress: number;
  done: number;
  cancelled: number;
  overdue: number;
  dueWithin30Days: number;
  onTimeCompletionRatio: number;
}

export interface OwnershipMetrics {
  policyOwners: number;
  policiesWithoutOwner: number;
  evidenceOwners: number;
  findingOwners: number;
  remediationAssignees: number;
}

export interface VerificationBacklogMetrics {
  proposedMappings: number;
  submittedEvidence: number;
  unverifiedImpactItems: number;
  openFindings: number;
  total: number;
}

/**
 * Informational only. The backend excludes confidence from readiness scoring
 * entirely; the UI must present it the same way and never derive a score,
 * ranking or status from it.
 */
export interface AiAssuranceMetrics {
  informationalOnly: true;
  aiSuggestedMappings: number;
  humanVerifiedAiMappings: number;
  rejectedAiMappings: number;
  pendingAiMappings: number;
  manualMappings: number;
  confidenceDistribution: {
    high: number;
    medium: number;
    low: number;
    unscored: number;
  };
}

export interface GovernanceDashboard {
  organizationId: string;
  generatedAt: string;
  policyHealth: PolicyHealthMetrics;
  frameworkCoverage: FrameworkCoverageMetric[];
  evidence: EvidenceMetrics;
  exceptions: ExceptionMetrics;
  readiness: ReadinessMetric[];
  findings: FindingMetrics;
  regulatory: RegulatoryMetrics;
  remediation: RemediationMetrics;
  ownership: OwnershipMetrics;
  verificationBacklog: VerificationBacklogMetrics;
  aiAssurance: AiAssuranceMetrics;
}

export type AttentionSeverity = "info" | "warning" | "critical";

export interface ExecutiveDashboard {
  organizationId: string;
  generatedAt: string;
  headline: {
    policyCoverageRatio: number;
    evidenceFreshnessRatio: number;
    averageReadinessScore: number | null;
    openFindings: number;
    overdueWork: number;
    verificationBacklog: number;
  };
  attention: { key: string; label: string; count: number; severity: AttentionSeverity }[];
  dashboard: GovernanceDashboard;
}

// ----- Executive reporting -------------------------------------------------

export type ReportType =
  | "governance_dashboard"
  | "executive_summary"
  | "framework_coverage"
  | "evidence_aging"
  | "exception_register"
  | "regulatory_exposure"
  | "audit_readiness"
  | "remediation_status";

/**
 * `xlsx` and `pdf` exist in the contract but are NOT implemented without a
 * platform renderer. Never offer a format the API has not advertised as
 * supported — the backend rejects it at definition creation.
 */
export type ReportFormat = "csv" | "manifest_json" | "deck_json" | "xlsx" | "pdf";

export type ReportTrigger = "manual" | "scheduled";
export type ReportRunStatus = "running" | "succeeded" | "failed";

export interface ReportDefinition {
  id: string;
  organizationId: string;
  definitionKey: string;
  name: string;
  description: string;
  reportType: ReportType;
  parameters: Record<string, unknown>;
  formats: ReportFormat[];
  scheduleIntervalDays: number | null;
  nextRunAt: string | null;
  schedulerJobId: string | null;
  recipients: string[];
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportRun {
  id: string;
  organizationId: string;
  definitionId: string | null;
  reportType: ReportType;
  parameters: Record<string, unknown>;
  trigger: ReportTrigger;
  status: ReportRunStatus;
  inputsHash: string;
  rowCount: number;
  durationMs: number;
  error: string;
  requestedBy: string;
  startedAt: string;
  completedAt: string | null;
}

export interface ReportArtifact {
  id: string;
  organizationId: string;
  runId: string;
  format: ReportFormat;
  mediaType: string;
  contentHash: string;
  byteSize: number;
  renderer: string;
  documentId: string | null;
  createdAt: string;
}

export interface ReportDownload {
  id: string;
  organizationId: string;
  artifactId: string;
  actorId: string;
  contentHash: string;
  byteSize: number;
  downloadedAt: string;
}

/** Outcome of one scheduled sweep. A repeated sweep reports nothing due. */
export interface ScheduledReportSweepResult {
  due: number;
  executed: number;
  failed: number;
  runIds: string[];
}

export interface CreateReportDefinitionInput {
  definitionKey: string;
  name: string;
  description?: string;
  reportType: ReportType;
  parameters?: Record<string, unknown>;
  formats: ReportFormat[];
  scheduleIntervalDays?: number | null;
  recipients?: string[];
}

export class GovernanceClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions | GovernanceHttp) {
    this.http = opts instanceof GovernanceHttp ? opts : new GovernanceHttp(opts);
  }

  // ----- Dashboards --------------------------------------------------------
  dashboard(orgId: string) {
    return this.http.get<{ dashboard: GovernanceDashboard }>(orgId, "governance/dashboard");
  }
  executiveDashboard(orgId: string) {
    return this.http.get<{ executiveDashboard: ExecutiveDashboard }>(
      orgId,
      "governance/executive-dashboard",
    );
  }

  // ----- Report definitions ------------------------------------------------
  reportDefinitions(orgId: string) {
    return this.http.get<{ reportDefinitions: ReportDefinition[]; supportedFormats: ReportFormat[] }>(
      orgId,
      "report-definitions",
    );
  }
  createReportDefinition(orgId: string, input: CreateReportDefinitionInput) {
    return this.http.post<{ reportDefinition: ReportDefinition }>(orgId, "report-definitions", input);
  }
  reportDefinition(orgId: string, definitionId: string) {
    return this.http.get<{ reportDefinition: ReportDefinition }>(
      orgId,
      `report-definitions/${definitionId}`,
    );
  }
  setReportDefinitionEnabled(orgId: string, definitionId: string, enabled: boolean) {
    return this.http.post<{ reportDefinition: ReportDefinition }>(
      orgId,
      `report-definitions/${definitionId}/enabled`,
      { enabled },
    );
  }
  runReportDefinition(orgId: string, definitionId: string) {
    return this.http.post<{ run: ReportRun; artifacts: ReportArtifact[] }>(
      orgId,
      `report-definitions/${definitionId}/run`,
    );
  }

  // ----- Runs and artifacts ------------------------------------------------
  reportRuns(orgId: string, filter: { definitionId?: string; limit?: number } = {}) {
    return this.http.get<{ reportRuns: ReportRun[] }>(
      orgId,
      `report-runs${queryString({ definitionId: filter.definitionId, limit: filter.limit })}`,
    );
  }
  /**
   * Runs every schedule that is due. The backend claims each slot before it
   * executes, so calling this twice produces one run, not two.
   */
  runScheduledReports(orgId: string) {
    return this.http.post<{ result: ScheduledReportSweepResult }>(
      orgId,
      "report-runs/scheduled/run",
    );
  }
  reportRun(orgId: string, runId: string) {
    return this.http.get<{ run: ReportRun; artifacts: ReportArtifact[] }>(orgId, `report-runs/${runId}`);
  }
  artifactDownloads(orgId: string, artifactId: string) {
    return this.http.get<{ downloads: ReportDownload[] }>(
      orgId,
      `report-artifacts/${artifactId}/downloads`,
    );
  }

  downloadArtifact(orgId: string, artifactId: string): Promise<DownloadedArtifact> {
    return this.http.download(
      orgId,
      `report-artifacts/${artifactId}/download`,
      `report-${artifactId}`,
    );
  }
}
